import { useRef } from 'react';
import getCookieValue from '~/utils/getCookieValue';
import { hashSha256 } from '~/utils/sha256';
import { generateUUID } from '~/utils/uuid';
import { useNonRevalidatingFetcher } from './useNonRevalidatingFetcher';

const buildEmailHashSha256 = (email?: string): string | undefined => {
  if (!email) return undefined;
  try {
    return hashSha256(email.toLowerCase());
  } catch {
    return undefined;
  }
};

// Enum for analytics event types
enum AnalyticsEventType {
  PAGEVIEW = 'pageview',
  CLICK = 'click',
}

// Types for analytics events
interface AnalyticsEvent {
  user_agent: string;
  url: string;
  event: AnalyticsEventType;
  timestamp: number;
  event_id: string;
  nectar_id: string;
  publication_id: string;
  visit_token: string;
  click_type?: string;
  click_url?: string;
  sendable_id?: string;
  subscription_id?: number;
  email_hash_sha256?: string;
}

interface UseNectarAnalyticsProps {
  nectarId: string;
  enabled: boolean;
  publicationId: string;
  postId?: string;
  subscriptionId?: number;
  email?: string;
}

interface UseNectarAnalyticsReturn {
  trackPageView: () => void;
  trackClick: (clickData: { clickType: string; clickUrl?: string }) => void;
}

// Hook for tracking analytics events
export const useNectarAnalytics = ({
  nectarId,
  enabled,
  publicationId,
  postId,
  subscriptionId,
  email,
}: UseNectarAnalyticsProps): UseNectarAnalyticsReturn => {
  const lastPageViewRef = useRef<string>('');

  const fetcher = useNonRevalidatingFetcher();

  // Extract visit token from existing visit_token cookie
  const extractVisitToken = (): string | null => {
    if (typeof document === 'undefined') return null;

    return getCookieValue('visit_token', document.cookie);
  };

  // Send event to analytics endpoint
  const sendEvent = (event: AnalyticsEvent): void => {
    fetcher.submit(JSON.stringify([event]), {
      method: 'post',
      action: '/nectar_analytics',
      encType: 'application/json',
    });
  };

  // Create base event with common properties
  const createBaseEvent = (eventType: AnalyticsEventType): AnalyticsEvent | null => {
    const visitToken = extractVisitToken();
    if (!visitToken) {
      return null;
    }

    const baseEvent: AnalyticsEvent = {
      user_agent: navigator.userAgent,
      url: window.location.href,
      event: eventType,
      timestamp: Date.now(),
      event_id: generateUUID(),
      nectar_id: nectarId,
      publication_id: publicationId,
      visit_token: visitToken,
      sendable_id: postId,
      subscription_id: subscriptionId,
      email_hash_sha256: buildEmailHashSha256(email),
    };

    return baseEvent;
  };

  // Track page view
  const trackPageView = (): void => {
    const currentUrl = window.location.href;

    // Don't track duplicate page views for the same URL
    if (currentUrl === lastPageViewRef.current) return;
    lastPageViewRef.current = currentUrl;

    const event = createBaseEvent(AnalyticsEventType.PAGEVIEW);
    if (!event) return;

    sendEvent(event);
  };

  // Track click event
  const trackClick = ({ clickType, clickUrl }: { clickType: string; clickUrl?: string }): void => {
    const event = createBaseEvent(AnalyticsEventType.CLICK);
    if (!event) return;

    event.click_type = clickType;
    event.click_url = clickUrl;
    sendEvent(event);
  };

  // Early return with no-op methods if analytics is disabled
  if (!enabled) {
    return {
      trackPageView: () => {},
      trackClick: () => {},
    };
  }

  return {
    trackPageView,
    trackClick,
  };
};

export type { AnalyticsEvent };
